<template>
  <section class="overflow-auto p-3 pretty-scroll-theme">
    <div class="text-white float-end hstack gap-1">
      <template v-if="votingRoundReport && !votingRoundReport.publishedAt">
        <AsyncButton class="btn btn-success" @click="publishVotingRoundReport">{{ $t('js.backend.live.make_public') }}</AsyncButton>
      </template>

      <div v-if="!this.votingRoundReport && !votingRound.archived">
        <button v-if="isVotingRoundOpened" class="btn btn-warning" @click.prevent="closeVotingRound">{{ $t("js.actions.close") }}</button>
        <button v-else class="btn btn-success" @click.prevent="openVotingRound">{{ $t("js.actions.open") }}</button>
      </div>
    </div>
    <header :title="votingRound.reference">
      <div class="h1 mb-0">{{ label }}</div>
      <div class="mb-3">
        <span class="badge text-bg-primary" v-if="votingRoundReport && votingRoundReport.publishedAt">
          <span v-text="$t('js.backend.live.voting_round_report.published')"/>:
          <time :datetime="votingRoundReport.publishedAt" v-text="new Date(votingRoundReport.publishedAt).toLocaleString()"/>
        </span>
        <span class="badge text-bg-warning" v-else v-text="$t('js.backend.live.voting_round_report.not_published')"/>
      </div>
    </header>
    <dl style="display: grid; grid-auto-columns: 1fr; grid-template-rows: auto auto; grid-auto-flow: column">
      <dt v-text="openedAtLabel"></dt>
      <dd>
        <time v-if="votingRound.openedAt" :datetime="formattedDate(votingRound.openedAt)"
              v-text="formattedDate(votingRound.openedAt)"></time>
        <span v-else class="text-white-50">{{ formattedDate(votingRound.openedAt) }}</span>
      </dd>
      <dt v-text="closedAtLabel"></dt>
      <dd>
        <time v-if="votingRound.closedAt" :datetime="formattedDate(votingRound.closedAt)"
              v-text="formattedDate(votingRound.closedAt)"></time>
        <span v-else class="text-white-50">{{ formattedDate(votingRound.closedAt) }}</span>
      </dd>
      <dt>Contests</dt>
      <dd>
        <template v-if="contestsInRound">
          <p
              v-for="contest in contestsInRound"
              class="badge text-bg-secondary mb-0 w-100"
          >
            {{ contest.title[firstAvailableLocale] }}
          </p>
        </template>
        <span v-else class="text-white-50">[ blank ]</span>
      </dd>
    </dl>

    <!-- Accordion -->
    <div id="accordionVotingRound" class="accordion accordion-dark">

      <!-- Results -->
      <div class="accordion-item">
        <h2 class="accordion-header" id="headingResult">
          <button class="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseResult" aria-expanded="true" aria-controls="collapseResult">
            {{ $t("js.backend.live.voting_round_report.results") }}
          </button>
        </h2>
        <div id="collapseResult" class="bg-blur-dark accordion-collapse collapse show" aria-labelledby="headingResult" data-parent="#accordionVotingRound">
          <div class="p-3">
            <div class="text-end">
              <a v-if="votingRoundReport && votingRoundReport.status === 'finished'" class="btn btn-primary"
                 :href="`../voting_rounds/${this.votingRound.id}/result`">
                {{ $t("js.backend.live.results.download") }}
              </a>
              <button v-if="votingRoundReport && !votingRound.managedBySlide" class="btn btn-danger" @click="deleteResults">
                {{ $t("js.backend.live.results.delete") }}
              </button>
            </div>
            <p v-if="currentState === 'open'">{{ $t("js.backend.live.voting_round_report.voting_round_open") }}</p>
            <p v-else-if="currentState === 'future'">{{ $t("js.backend.live.voting_round_report.voting_round_future") }}</p>
            <template v-else-if="!votingRoundReport">
              <p v-if="votingRound.voteCount === 0">{{ $t("js.backend.live.voting_round_report.no_votes") }}</p>
              <p v-else>{{ $t("js.backend.live.voting_round_report.no_report") }}</p>
              <AsyncButton v-show="currentState === 'closed'" class="btn btn-primary" @click="createVotingRoundReport">
                {{ $t("js.backend.live.voting_round_report.calculate_result") }}
              </AsyncButton>
            </template>
          </div>

          <template v-if="votingRoundReport && currentState === 'closed'">
            <div v-if="votingRoundReport.resultOrchestrators.length === 0"
                class="p-3 text-center">
              <IndeterminateSpinner class="h3" spinner-color="white" track-color="gray" line-thickness="2" />
              <div> {{ $t("js.backend.live.voting_round_report.starting") }}</div>
            </div>
            <div class="p-3"
                v-for="(resultOrchestrator, index) in votingRoundReport.resultOrchestrators">
              <hr class="mt-0" v-if="index > 0">
              <VotingRoundResult :result-orchestrator="resultOrchestrator"
                                 :contest="contests.find(contest => contest.reference === resultOrchestrator.contestReference)" />
            </div>
            <hr class="mt-0">
            <section v-if="showTurnout" class="w-100 p-3">
              <header class="h4 w-100" v-text="$t('js.backend.live.voting_round_report.turnout')"></header>
              <TurnoutTable :turnout="turnout" :theme="theme"/>
            </section>
          </template>
        </div>
      </div>

      <!-- Statistics -->
      <div class="accordion-item" v-if="election.statisticsClientUrl">
        <h2 class="accordion-header" id="headingStats">
          <button class="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseStats" aria-expanded="true" aria-controls="collapseStats">
            {{ $t("js.backend.live.statistics") }}
          </button>
        </h2>
        <div id="collapseStats" class="accordion-collapse collapse" aria-labelledby="headingStats" data-parent="#accordionVotingRound">
          <div>
            <Statistics :statisticsUrl="statisticsUrl" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import IndeterminateSpinner from "./IndeterminateSpinner.vue";
import VotingRoundResult from "./VotingRoundResult.vue";
import { mapState, mapGetters } from "vuex";
import { calculateState, label } from "../../../entrypoints/shared/voting_rounds_helper";
import AsyncButton from "../../shared/AsyncButton.vue";
import Statistics from "./Statistics.vue"
import apiClient from "../../../entrypoints/frontend/apiClient";
import TurnoutTable from './TurnoutTable.vue';
import { orderContests } from '../../../entrypoints/shared/contest_utilities';

export default {
  name: "VotingRoundReport",
  components: { TurnoutTable, AsyncButton, IndeterminateSpinner, VotingRoundResult, Statistics },
  props: {
    votingRound: {
      type: Object,
      required: true,
    },
    showTurnout: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapState(["votingRoundReports", "contests", "election"]),
    ...mapGetters(['firstAvailableLocale']),
    label() {
      return label(this.votingRound, this.$i18n.locale);
    },
    currentState() {
      return calculateState(this.votingRound);
    },
    contestsInRound() {
      let filteredContests = this.contests.filter(c => this.votingRound.contestReferences.includes(c.reference))
      return this.orderContests(filteredContests, this.votingRound.contestPositions)
    },
    closedAtLabel() {
      let closedAt = this.votingRound.closedAt;
      if (closedAt == null || closedAt < Date.now()) {
        return this.$t("js.backend.live.voting_rounds_sheet.closed_at_time.past");
      } else {
        return this.$t("js.backend.live.voting_rounds_sheet.closed_at_time.future");
      }
    },
    openedAtLabel() {
      let openedAt = this.votingRound.openedAt;
      if (openedAt == null || openedAt < Date.now()) {
        return this.$t("js.backend.live.voting_rounds_sheet.opened_at_time.past");
      } else {
        return this.$t("js.backend.live.voting_rounds_sheet.opened_at_time.future");
      }
    },
    votingRoundReport() {
      return this.votingRoundReports.find(vrr => vrr.votingRoundReference === this.votingRound.reference);
    },
    statisticsUrl() {
      return `${this.election.statisticsClientUrl}/${this.votingRound.reference}/tables/overview`
    },
    isVotingRoundOpened() {
      return this.votingRound.status === "open";
    },
    resultOrchestrator() {
      return this.votingRoundReport.resultOrchestrators[this.votingRoundReport.resultOrchestrators.length-1];
    },
    turnout() {
      return this.resultOrchestrator.result.voterCounts
    },
  },
  methods: {
    orderContests,
    async createVotingRoundReport(publishAt=null) {
      return await apiClient.post(`../voting_rounds/${this.votingRound.id}/voting_round_reports`, {
        voting_round_report: {
          published_at: publishAt,
          voting_round_reference: this.votingRound.reference,
        },
      });
    },
    async publishVotingRoundReport(){
      await apiClient.patch(`../voting_rounds/${this.votingRound.id}/voting_round_reports/${this.votingRoundReport.id}`, {
        voting_round_report: { published_at: new Date().toISOString() }
      })
    },
    async openVotingRound(){
      await apiClient.put(`../voting_rounds/${this.votingRound.id}`, { votingRound: { status: "open" } })
    },
    async closeVotingRound(){
      await apiClient.put(`../voting_rounds/${this.votingRound.id}`, { votingRound: { status: "closed" } })
    },
    async deleteResults(){
      const formData = new FormData();
      formData.append('_method', 'delete');
      await apiClient.post(`../voting_rounds/${this.votingRound.id}/voting_round_reports/${this.votingRoundReport.id}`, formData)
    },
    formattedDate(date) {
      return date ? date.toLocaleString() : "[ blank ]"
    }
  },
};
</script>

<style>
.accordion-dark {
  --bs-accordion-bg: transparent;
  --bs-accordion-color: var(--bs-white);
  --bs-accordion-active-bg: var(--av-theme-background);
  --bs-accordion-active-color: var(--av-theme-text);
  --bs-accordion-border-color: #9B9F9B;
  --bs-accordion-btn-bg: rgba(30, 30, 30, 0.8);
  --bs-accordion-btn-color: var(--bs-light);
  --bs-accordion-btn-focus-border-color: #000000;
  --bs-accordion-btn-focus-box-shadow: 0 0 0 0.25rem rgba(0, 0, 0, 0.25);
  --bs-accordion-btn-icon: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23818181'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
  --bs-accordion-btn-active-icon: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23b2b2b2'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
}
</style>
